// src/pages/leads/useCreateLeadPage.ts
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateLeadMutation } from "@/api/features/leads/mutations";
import type {
  CreateLeadBody,
  LeadPriority,
  LeadSource,
  LeadStatus,
} from "@/api/features/leads/types";
import type { UpsertLead } from "@/components/leads/forms/CreateLeadForm";

/* =========================
 * UI → API enum mappers
 * ========================= */

function mapStatusUiToApi(status: UpsertLead["status"]): LeadStatus {
  switch (status) {
    case "Completed":
      return "COMPLETED";
    case "Cancelled":
      return "CANCELLED";
    case "Pending":
    default:
      return "PENDING";
  }
}

function mapSourceUiToApi(
  source?: UpsertLead["source"]
): LeadSource | undefined {
  if (!source) return undefined;

  switch (source) {
    case "Walk-in":
      return "WALK_IN";
    case "Referral":
      return "REFERRAL";
    case "Online":
      return "ONLINE";
    case "Advertisement":
      return "ADVERTISEMENT";
    case "Social Media":
      return "SOCIAL_MEDIA";
    case "Partner":
      return "PARTNER";
    case "Phone Inquiry":
      return "PHONE_INQUIRY";
    case "Email Campaign":
      return "EMAIL_CAMPAIGN";
    case "Other":
      return "OTHER";
    default:
      return undefined;
  }
}

function mapPriorityUiToApi(
  priority?: UpsertLead["priority"]
): LeadPriority | undefined {
  if (!priority) return undefined;

  switch (priority) {
    case "Low":
      return "LOW";
    case "Medium":
      return "MEDIUM";
    case "High":
      return "HIGH";
    case "Urgent":
      return "URGENT";
    default:
      return undefined;
  }
}

/* =========================
 * Shared API error extractor
 * (same pattern as suppliers)
 * ========================= */

type ApiValidationError = {
  ok?: boolean;
  message?: string;
  errors?: {
    formErrors?: string[];
    fieldErrors?: Record<string, string[]>;
  };
};

function extractErrorMessage(err: unknown): string | null {
  if (!err) return null;

  const anyErr = err as any;

  // Support multiple shapes:
  // - axios-style → err.response.data
  // - custom wrapper → err.data / err.payload
  const data: ApiValidationError | undefined =
    anyErr?.response?.data ?? anyErr?.data ?? anyErr?.payload ?? undefined;

  if (!data) {
    const msg = anyErr?.message ?? String(err);
    return msg || null;
  }

  const messages: string[] = [];

  if (data.message) {
    messages.push(data.message);
  }

  if (data.errors?.formErrors && data.errors.formErrors.length > 0) {
    messages.push(...data.errors.formErrors);
  }

  if (data.errors?.fieldErrors) {
    for (const [field, errs] of Object.entries(data.errors.fieldErrors)) {
      if (!errs || errs.length === 0) continue;
      errs.forEach((m) => {
        messages.push(`${field}: ${m}`);
      });
    }
  }

  if (messages.length === 0) {
    return null;
  }

  // Your form currently treats "|" as a splitter for bullets if needed
  return messages.join(" | ");
}

/* =========================
 * Hook
 * ========================= */

export function useCreateLeadPage() {
  const navigate = useNavigate();
  const createMutation = useCreateLeadMutation();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const submitting = createMutation.isPending;

  const handleSubmit = async (payload: UpsertLead) => {
    setErrorMessage(undefined);

    // 🔹 Local validation (no alerts)
    if (!payload.assignedToUserId) {
      setErrorMessage("Please select a lead owner for this lead.");
      return;
    }

    if (!payload.products || payload.products.length === 0) {
      setErrorMessage("Please select at least one product for this lead.");
      return;
    }

    // 🔹 UI → API body mapping
    const body: CreateLeadBody = {
      firstName: payload.firstName.trim(),
      lastName: payload.lastName.trim(),
      email: payload.email.trim(),
      phone: payload.phone.trim(),
      leadDate: payload.date, // already YYYY-MM-DD
      assignedToUserId: payload.assignedToUserId,
      products: payload.products.map((pid) => {
        if (
          payload.visaLeadProductId &&
          pid === payload.visaLeadProductId &&
          payload.visaDetailsDraft
        ) {
          return {
            productId: pid,
            visaDetails: {
              countryIso2: payload.visaDetailsDraft.countryIso2?.trim() || null,
              enquiryAt: payload.visaDetailsDraft.enquiryAt || null,
              docsReceivedAt: payload.visaDetailsDraft.docsReceivedAt || null,
              docsSubmittedAt: payload.visaDetailsDraft.docsSubmittedAt || null,
              docsCollectedAt: payload.visaDetailsDraft.docsCollectedAt || null,
              appointmentAt: payload.visaDetailsDraft.appointmentAt || null,
              invoiceAmount:
                payload.visaDetailsDraft.invoiceAmount !== ""
                  ? Number(payload.visaDetailsDraft.invoiceAmount)
                  : null,
              receiptAmount:
                payload.visaDetailsDraft.receiptAmount !== ""
                  ? Number(payload.visaDetailsDraft.receiptAmount)
                  : null,
              loginId: payload.visaDetailsDraft.loginId?.trim() || null,
              loginPassword: payload.visaDetailsDraft.loginPassword?.trim() || null,
              remark: payload.visaDetailsDraft.remark?.trim() || null,
            },
          };
        }
        return { productId: pid };
      }),

      status: mapStatusUiToApi(payload.status),
      source: mapSourceUiToApi(payload.source),
      priority: mapPriorityUiToApi(payload.priority),
      notes: payload.notes?.trim() || undefined,
    };

    try {
      await createMutation.mutateAsync(body);
      navigate("/leads");
    } catch (err: any) {
      setErrorMessage(
        extractErrorMessage(err) ?? "Failed to create lead."
      );
    }
  };

  const handleCancel = () => {
    navigate("/leads");
  };

  // Optional debug logging (same style as suppliers)
  useEffect(() => {
    if (createMutation.error) {
      // eslint-disable-next-line no-console
      console.error("[CreateLead] mutation error:", createMutation.error);
    }
  }, [createMutation.error]);

  return {
    handleSubmit,
    handleCancel,
    submitting,
    errorMessage,
  };
}
